import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import TechDashboard from "./pages/TechDashboard";
import ManagerDashboard from "./pages/ManagerDashboard";
import { Toaster } from "./components/ui/sonner";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Set up axios defaults with timeout
axios.defaults.baseURL = API;
axios.defaults.timeout = 60000; // 60 second timeout for slow connections

// Add response interceptor for better error handling
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
    }
    // Only clear token on explicit token expired
    if (error.response?.status === 401 && error.response?.data?.detail === "Token expired") {
      localStorage.removeItem("token");
      localStorage.removeItem("cachedUser");
      delete axios.defaults.headers.common["Authorization"];
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser(token);
    } else {
      setLoading(false);
    }
    
    // Register service worker for PWA installation
    serviceWorkerRegistration.register();
  }, []);

  const fetchUser = async (token, retryCount = 0) => {
    try {
      const response = await axios.get("/auth/me");
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      
      // Only logout on explicit 401 unauthorized, not on network errors
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
        setLoading(false);
      } else if (retryCount < 2) {
        // Retry on network errors (up to 2 retries)
        console.log(`Retrying fetch user... attempt ${retryCount + 1}`);
        setTimeout(() => fetchUser(token, retryCount + 1), 1500);
      } else {
        // After retries, keep user logged in if token exists (offline mode)
        setLoading(false);
        // Try to use cached user data if available
        const cachedUser = localStorage.getItem("cachedUser");
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            console.error("Failed to parse cached user");
          }
        }
      }
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("cachedUser", JSON.stringify(userData));
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("cachedUser");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                user.role === "tech" ? (
                  <Navigate to="/tech" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              user ? (
                <Navigate
                  to={user.role === "tech" ? "/tech" : "/dashboard"}
                  replace
                />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/tech"
            element={
              user && user.role === "tech" ? (
                <TechDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user && (user.role === "admin" || user.role === "part_manager" || user.role === "dispatcher") ? (
                <ManagerDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Legacy route for backwards compatibility */}
          <Route
            path="/manager"
            element={<Navigate to="/dashboard" replace />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
